import React, { useState, useEffect, useMemo, useRef } from "react";
import { View, TouchableOpacity } from "react-native";
import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppUser from "@toothfairy/shared-ui/AppUser";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";

const PricingCard = ({
  subscription,
  isSelected,
  onSelect,
  currentSeats = 1,
  onSeatsChange,
  isUpdate = false,
  disabled = false,
  isCurrentPlan = false,
  currentPlan,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const [seats, setSeats] = useState(currentSeats);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { user } = AppUser.useUser();
  const creditBalance = user?.stripeCustomerData?.balance / -100;

  // Store initial values in refs - they won't change during re-renders
  const initialSeatsRef = useRef(currentSeats);
  const initialPlanRef = useRef(currentPlan);
  useEffect(() => {
    if (subscription.pricingModel === "perSeat") {
      setTotalPrice(subscription.price * seats);
    }
  }, [seats, subscription.price, subscription.pricingModel]);

  useEffect(() => {
    setSeats(currentSeats);
  }, [currentSeats]);

  // Update refs when entering update mode
  useEffect(() => {
    if (isUpdate) {
      initialSeatsRef.current = currentSeats;
      initialPlanRef.current = currentPlan;
    }
  }, [isUpdate, currentSeats, currentPlan]);

  const handleSeatsChange = (newSeats) => {
    console.log(
      `[handleSeatsChange ${subscription.type}] changing seats from ${seats} to ${newSeats}`
    );
    console.log(
      `[handleSeatsChange ${subscription.type}] currentSeats: ${currentSeats}`
    );
    setSeats(newSeats);
    if (onSeatsChange) {
      onSeatsChange(newSeats, subscription.type);
    }
  };

  const isPopular = subscription.type === "pro";
  const isEnterprise = subscription.type === "enterprise";

  // Helper function to get subscription hierarchy level
  const getSubscriptionLevel = (subscriptionType) => {
    switch (subscriptionType) {
      case "base":
        return 1;
      case "pro":
        return 2;
      case "enterprise":
        return 3;
      default:
        return 0;
    }
  };

  // Determine if this is an upgrade or downgrade
  const isUpgrade =
    currentPlan &&
    getSubscriptionLevel(subscription.type) > getSubscriptionLevel(currentPlan);
  const isDowngrade =
    currentPlan &&
    getSubscriptionLevel(subscription.type) < getSubscriptionLevel(currentPlan);

  // Check if there's actually a change (different plan or different seats)
  const hasChanges = useMemo(() => {
    if (!isUpdate) return true; // Not in update mode, always allow
    const planChanged = subscription.type !== initialPlanRef.current;
    const seatsChanged = seats !== initialSeatsRef.current;
    console.log(
      `[PricingCard ${subscription.type}] planChanged: ${planChanged}, seatsChanged: ${seatsChanged}`
    );
    console.log(
      `[PricingCard ${subscription.type}] seats: ${seats}, initialSeats: ${initialSeatsRef.current}`
    );
    console.log(
      `[PricingCard ${subscription.type}] subscription.type: ${subscription.type}, initialPlan: ${initialPlanRef.current}`
    );
    console.log(
      `[PricingCard ${subscription.type}] isCurrentPlan: ${isCurrentPlan}, isUpdate: ${isUpdate}`
    );
    return planChanged || seatsChanged;
  }, [isUpdate, subscription.type, seats]);

  // Determine if button should be disabled
  const isButtonDisabled = useMemo(() => {
    const result =
      disabled ||
      !hasChanges ||
      (isEnterprise && currentPlan === "enterprise" && isDowngrade);
    console.log(
      `[PricingCard ${subscription.type}] hasChanges: ${hasChanges}, isButtonDisabled: ${result}`
    );
    console.log(
      `[PricingCard ${
        subscription.type
      }] disabled: ${disabled}, !hasChanges: ${!hasChanges}, enterprise downgrade check: ${
        isEnterprise && currentPlan === "enterprise" && isDowngrade
      }`
    );
    return result;
  }, [disabled, hasChanges, isEnterprise, currentPlan, isDowngrade]);

  return (
    <TouchableOpacity
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: currentTheme?.theme?.backgroundColor,
        borderRadius: 12,
        borderWidth: isSelected || isHovered ? 1 : 1,
        borderColor:
          isSelected || isHovered
            ? currentTheme?.theme?.primary
            : currentTheme?.theme?.borderColor || "#E0E0E0",
        padding: 24,
        margin: 8,
        minWidth: 300,
        maxWidth: 350,
        position: "relative",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {isPopular && !isCurrentPlan && currentPlan !== "enterprise" && (
        <View
          style={{
            position: "absolute",
            top: -12,
            left: 20,
            right: 20,
            backgroundColor: currentTheme?.theme?.primary || "#007AFF",
            borderRadius: 20,
            paddingVertical: 6,
            paddingHorizontal: 16,
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <AppText
            style={{
              color: "white",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            Most Popular
          </AppText>
        </View>
      )}

      {isCurrentPlan && (
        <View
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            backgroundColor: currentTheme?.theme?.orange || "#4CAF50",
            borderRadius: 12,
            paddingVertical: 4,
            paddingHorizontal: 8,
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <AppText
            style={{
              color: "white",
              fontSize: 10,
              fontWeight: "bold",
            }}
          >
            Current Plan
          </AppText>
        </View>
      )}

      {/* Main content container - will grow to fill available space */}
      <View
        style={{
          flex: 1,
          flexDirection: "column",
        }}
      >
        <View
          style={{
            alignItems: "center",
            marginBottom: 20,
            marginTop: isPopular ? 0 : 0,
          }}
        >
          <AppText
            preventTranslation
            style={{
              fontSize: 24,
              fontWeight: "bold",
              color: currentTheme?.theme?.font_color,
              marginBottom: 8,
            }}
          >
            {subscription.title}
          </AppText>

          <View
            style={{
              flexDirection: "row",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            {!isEnterprise ? (
              <>
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 32,
                    fontWeight: "bold",
                    color: currentTheme?.theme?.primary || "#007AFF",
                  }}
                >
                  {subscription.currency}
                  {subscription.price.toFixed(2)}
                </AppText>
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 14,
                    color:
                      currentTheme?.theme?.secondary_font_color ||
                      currentTheme?.theme?.font_color,
                    marginLeft: 4,
                  }}
                >
                  /{subscription.unit}
                </AppText>
              </>
            ) : (
              <AppText
                style={{
                  fontSize: 32,
                  fontWeight: "bold",
                  color: currentTheme?.theme?.primary || "#007AFF",
                }}
              >
                Custom
              </AppText>
            )}
          </View>

          <AppText
            preventTranslation
            style={{
              fontSize: 14,
              color:
                currentTheme?.theme?.secondary_font_color ||
                currentTheme?.theme?.font_color,
              marginBottom: 4,
            }}
          >
            {subscription.billingCycle}
          </AppText>
        </View>

        <AppText
          preventTranslation
          style={{
            fontSize: 12,
            color: currentTheme?.theme?.font_color,
            textAlign: "center",
            marginBottom: 20,
            lineHeight: 20,
            fontWeight: "bold",
          }}
        >
          {subscription.description}
        </AppText>

        {!isEnterprise && (
          <View style={{ marginBottom: 20 }}>
            <AppText
              preventTranslation
              style={{
                fontSize: 14,
                fontWeight: "bold",
                color: currentTheme?.theme?.font_color,
                marginBottom: 8,
              }}
            >
              Number of seats: {seats}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <AppIcon
                onPress={() =>
                  handleSeatsChange(
                    Math.max(subscription.initialSeats, seats - 1)
                  )
                }
                icon={AppAssets?.icons?.minus}
                color={
                  seats <= subscription.initialSeats
                    ? currentTheme?.theme?.mediumGrey
                    : currentTheme?.theme?.primary
                }
                isClickable
                isDisabled={seats <= subscription.initialSeats}
              />

              <AppText
                style={{
                  fontSize: 18,
                  fontWeight: "bold",
                  color: currentTheme?.theme?.font_color,
                  marginHorizontal: 20,
                }}
                preventTranslation
              >
                {seats}
              </AppText>
              <AppIcon
                onPress={() =>
                  handleSeatsChange(Math.min(subscription.maxSeats, seats + 1))
                }
                icon={AppAssets?.icons?.plusCircle}
                color={
                  seats >= subscription.maxSeats
                    ? currentTheme?.theme?.mediumGrey
                    : currentTheme?.theme?.primary
                }
                isClickable
                isDisabled={seats <= subscription.initialSeats}
              />
            </View>

            <AppText
              style={{
                fontSize: 16,
                fontWeight: "bold",
                color: currentTheme?.theme?.primary,
                textAlign: "center",
                marginTop: 12,
              }}
              preventTranslation
            >
              Total: {subscription.currency}
              {totalPrice.toFixed(2)}/month
            </AppText>
          </View>
        )}

        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: currentTheme?.theme?.borderColor || "#E0E0E0",
            paddingTop: 20,
          }}
        >
          <AppText
            style={{
              fontSize: 16,
              fontWeight: "bold",
              color: currentTheme?.theme?.font_color,
              marginBottom: 12,
            }}
            preventTranslation
          >
            What's included:
          </AppText>

          {subscription.pricingLeafs.map((feature, index) => (
            <View
              key={index}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                marginBottom: 8,
              }}
            >
              <AppText
                iconSize={12}
                leftIcon={AppAssets?.icons?.tick}
                style={{
                  marginLeft: 10,
                  fontSize: 14,
                  color: currentTheme?.theme?.font_color,
                  flex: 1,
                  fontWeight: feature.bold ? "bold" : "normal",
                }}
                preventTranslation
              >
                {feature.text}
              </AppText>
            </View>
          ))}
        </View>
      </View>
      {/* End of main content container */}

      {/* Button container - stays at bottom */}
      <View>
        <ToothFairyButton
          title={
            isUpdate && !isCurrentPlan
              ? isUpgrade
                ? subscription.type == "enterprise"
                  ? "Get in touch"
                  : "Upgrade"
                : isDowngrade
                ? "Downgrade"
                : "Update"
              : isUpdate && isCurrentPlan
              ? "Update"
              : !isEnterprise
              ? totalPrice <= creditBalance
                ? "Free with credits"
                : subscription.ctaLabel
              : subscription.ctaLabel
          }
          onPress={() =>
            !isButtonDisabled && onSelect && onSelect(subscription)
          }
          isDisabled={
            isButtonDisabled ||
            (isUpdate && currentPlan === "enterprise" && isDowngrade)
          }
          is_secondary={!isSelected}
          style={{
            marginTop: 20,
            borderRadius: 8,
            marginHorizontal: 0,
          }}
          wrapperStyle={{
            alignSelf: "center",
          }}
          // backgroundColor={
          //   isSelected ? currentTheme?.theme?.primary : "transparent"
          // }
        />

        {isUpdate && currentPlan === "enterprise" && (
          <AppText
            style={{
              fontSize: 12,
              color:
                currentTheme?.theme?.secondary_font_color ||
                currentTheme?.theme?.font_color,
              textAlign: "center",
              marginTop: 8,
              fontStyle: "italic",
            }}
            preventTranslation
          >
            {isDowngrade
              ? "Enterprise downgrades disabled, get in touch to discuss options"
              : "Enterprise seats updates disabled, get in touch to discuss pricing options"}
          </AppText>
        )}
      </View>
      {/* End of button container */}
    </TouchableOpacity>
  );
};

export default PricingCard;
